export const theme = {
  colors: {
    // Balanced Green-Blue Palette
    primary: '#3B82F6',           // Modern blue
    primaryDark: '#1E40AF',       // Darker blue for hover states
    primaryLight: '#60A5FA',      // Lighter blue for backgrounds
    secondary: '#1F2937',         // Professional dark gray
    accent: '#10B981',            // Modern green for success states
    success: '#00D4AA',           // Bright green for checkmarks
    successLight: '#34D399',      // Light green for accents

    // Background Colors
    background: {
      main: '#0F172A',            // Deep professional dark
      card: 'rgba(30, 41, 59, 0.8)',  // Subtle card background
      overlay: 'rgba(0, 0, 0, 0.75)'
    },

    // Text Colors - WCAG Compliant
    text: {
      primary: '#F8FAFC',         // High contrast white
      secondary: '#94A3B8',       // Muted text
      muted: '#64748B',           // Disabled text
      error: '#EF4444',           // Professional red
      success: '#10B981',         // Professional green
      accent: '#3B82F6'           // Accent blue
    },

    // Border Colors - Simplified system
    border: {
      default: 'rgba(148, 163, 184, 0.2)',  // Standard border
      focus: '#3B82F6',                     // Focus state
      error: '#EF4444',                     // Error state
      success: '#10B981'                    // Success state
    },

    // Modern Green-Blue Gradients
    gradient: {
      primary: 'linear-gradient(135deg, #3B82F6, #1E40AF)',      // Blue gradient
      card: 'linear-gradient(145deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.95))',  // Enhanced card gradient
      button: 'linear-gradient(135deg, #3B82F6, #1E40AF)',       // Button gradient
      buttonHover: 'linear-gradient(135deg, #2563EB, #1D4ED8)',  // Button hover
      success: 'linear-gradient(135deg, #00D4AA, #10B981)',       // Green success gradient
      error: 'linear-gradient(135deg, #EF4444, #DC2626)',         // Error gradient
      frame: 'linear-gradient(135deg, #00D4AA, #3B82F6)',         // Green-blue frame gradient
      frameSubtle: 'linear-gradient(135deg, rgba(0, 212, 170, 0.1), rgba(59, 130, 246, 0.1))'  // Subtle frame gradient
    },
    // Status Specific Colors
    status: {
      valid: {
        main: '#00FF80',
        background: '#1E3A3A',
        light: 'rgba(0, 255, 133, 0.1)',
        border: 'rgba(0, 255, 133, 0.3)'
      },
      expired: {
        main: '#FF4444',
        background: '#2D1A1A',
        light: 'rgba(255, 68, 68, 0.1)',
        border: 'rgba(255, 68, 68, 0.3)'
      },
      warning: {
        main: '#F59E0B',
        background: 'rgba(245, 158, 11, 0.1)',
        border: '#F59E0B'
      },
      urgent: {
        main: '#FF6B35',
        background: '#FF6B351A',
        border: '#FF6B354D'
      }
    }
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    xxl: '3rem'
  },

  // Mobile-First Responsive Breakpoints
  breakpoints: {
    mobile: '320px',
    mobileLarge: '480px',
    tablet: '768px',
    desktop: '1024px',
    desktopLarge: '1280px',
    desktopXLarge: '1536px'
  },

  // Mobile-First Media Queries
  media: {
    mobile: '@media (min-width: 320px)',
    mobileLarge: '@media (min-width: 480px)',
    tablet: '@media (min-width: 768px)',
    desktop: '@media (min-width: 1024px)',
    desktopLarge: '@media (min-width: 1280px)',
    desktopXLarge: '@media (min-width: 1536px)'
  },
  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    full: '50%'
  },
  shadows: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    subtle: '0 1px 3px rgba(0, 0, 0, 0.1)',
    prominent: '0 4px 12px rgba(0, 0, 0, 0.15)',
    glow: '0 0 0 1px rgba(59, 130, 246, 0.1)'
  },
  // Mobile-First Touch Targets & Accessibility
  mobile: {
    touchTarget: '44px',        // Minimum touch target size
    touchTargetLarge: '48px',   // Large touch targets
    spacing: {
      xs: '0.5rem',             // Increased for mobile
      sm: '0.75rem',
      md: '1rem',
      lg: '1.5rem',
      xl: '2rem'
    },
    fontSize: {
      xs: '0.875rem',           // Slightly larger for mobile
      sm: '1rem',
      md: '1.125rem',
      lg: '1.25rem',
      xl: '1.5rem'
    }
  },
  transitions: {
    fast: '0.15s ease',
    normal: '0.3s ease',
    slow: '0.5s ease',
    spring: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)'
  },
  typography: {
    fontFamily: {
      primary: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      mono: "'JetBrains Mono', 'Fira Code', monospace"
    },
    fontSize: {
      xs: '0.75rem',
      sm: '0.875rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.875rem',
      '4xl': '2.25rem',
      '5xl': '3rem'
    },
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700
    }
  },
  components: {
    stepGuidance: {
      background: 'rgba(59, 130, 246, 0.08)',
      border: '1px solid rgba(59, 130, 246, 0.25)',
      borderRadius: '12px',
      padding: '20px',
      margin: '20px 0',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden'
    },
    stepIcon: {
      background: 'rgba(59, 130, 246, 0.15)',
      color: '#3B82F6',
      width: '28px',
      height: '28px',
      borderRadius: '50%',
      fontSize: '14px',
      fontWeight: '600'
    },
    stepTitle: {
      color: '#3B82F6',
      fontSize: '16px',
      fontWeight: '600'
    },
    stepDescription: {
      color: '#94A3B8',
      fontSize: '14px',
      lineHeight: '1.5'
    },
    checkmark: {
      color: '#00D4AA',
      fontSize: '16px'
    }
  },
  buttons: {
    primary: {
      background: 'linear-gradient(135deg, #3B82F6, #1E40AF)',
      color: '#ffffff',
      shadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
      hoverShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
    },
    disabled: {
      background: 'linear-gradient(135deg, #EF4444, #DC2626)',
      color: '#ffffff',
      shadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
      hoverShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
    },
    processing: {
      background: 'linear-gradient(135deg, #6B7280, #9CA3AF)',
      color: '#ffffff',
      shadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
      hoverShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
    }
  }
};

